import { useEffect, useRef, useState } from 'react';

/**
 * The live width of an element (ResizeObserver), for "show this only if there
 * is room" decisions that depend on the ELEMENT's space rather than the
 * viewport's (a tile in a two-column grid is much narrower than the screen).
 * Returns `null` until measured (and where ResizeObserver does not exist), so
 * callers can choose the optimistic default.
 */
export function useElementWidth<T extends HTMLElement>(): [React.RefObject<T>, number | null] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (typeof next === 'number') setWidth(next);
    });
    observer.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}
